/*!
 * Omneo Lists — Core Module
 * -------------------------------------------------------------------------
 * Owns: config, Omneo/Shapes SDK auth, session (cookie) state, and the
 * public API other scripts use to add/remove/query list items.
 *
 * Does NOT touch the DOM beyond a few debug hooks. UI binding (button
 * clicks, accordions, etc.) lives in omneo-lists-ui.js and calls the
 * functions exposed on window.OmneoLists below.
 *
 * Dependencies (already loaded by the theme, not bundled here):
 *   - window.Cookies   (js-cookie — currently bundled at the top of theme.js)
 *   - window.ShapesSDK (Omneo's Shapes SDK, loaded via omneo-lists-init.liquid)
 *
 * See docs/omneo-lists-README.md for the full architecture writeup,
 * config reference, and event list.
 * -------------------------------------------------------------------------
 */
(function (window, document) {
  'use strict';

  if (window.OmneoLists) {
    console.warn('[OmneoLists] window.OmneoLists already exists — skipping duplicate init.');
    return;
  }

  // ---------------------------------------------------------------------
  // Config
  // ---------------------------------------------------------------------
  var DEFAULTS = {
    customerId: 0,
    isLoggedIn: false,
    omneoIdUrl: '',
    tokenProxyUrl: '/apps/cx',
    cookieExpiryDays: 7,
    cookiePrefix: 'omneo_list_',
    debug: false
  };

  var config = {};
  var shapesClient = null;
  var listeners = {};
  var authPromise = null;

  function readConfig() {
    var el = document.getElementById('omneo-lists-config');
    var parsed = {};
    if (el) {
      try {
        parsed = JSON.parse(el.textContent || '{}');
      } catch (e) {
        warn('Could not parse #omneo-lists-config JSON.', e);
      }
    } else {
      warn('No #omneo-lists-config element found — using defaults. Check omneo-lists-init.liquid is rendered.');
    }
    config = Object.assign({}, DEFAULTS, parsed);
  }

  function log() {
    if (!config.debug) return;
    console.log.apply(console, ['[OmneoLists]'].concat(Array.prototype.slice.call(arguments)));
  }

  function warn() {
    console.warn.apply(console, ['[OmneoLists]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ---------------------------------------------------------------------
  // Events — internal listeners + mirrored as DOM CustomEvents
  // (external theme code can hook in via document.addEventListener too)
  // ---------------------------------------------------------------------
  function on(event, cb) {
    (listeners[event] = listeners[event] || []).push(cb);
    return function off() {
      listeners[event] = (listeners[event] || []).filter(function (fn) { return fn !== cb; });
    };
  }

  function emit(event, detail) {
    log('event:', event, detail);
    (listeners[event] || []).forEach(function (cb) {
      try { cb(detail); } catch (e) { warn('Listener for "' + event + '" threw', e); }
    });
    try {
      document.dispatchEvent(new CustomEvent('omneolists:' + event, { detail: detail }));
    } catch (e) { /* CustomEvent unsupported — ignore, internal listeners still fired */ }
  }

  // ---------------------------------------------------------------------
  // Tier/status body classes
  //
  // The legacy omneo-lists.liquid's profileHandler added classes like
  // `cll-team-fave` / `cll-level-3` to <body>, and custom.css gates
  // .AddtoReserveList visibility on exactly those classes (see
  // ".is_customer.cll-team-fave .AddtoReserveList" etc). An earlier
  // version of this module only re-emitted profile:update as an event
  // without reproducing this DOM side effect, which silently hid the
  // Reserve List button for every tier — found via staging testing.
  // Registered here (not in the UI module) since it's core business
  // behaviour the CSS depends on, not just click-handler wiring.
  // ---------------------------------------------------------------------
  function applyProfileClasses(profile) {
    if (!profile) return;
    if (profile.tier_handle) {
      document.body.classList.add('cll-' + profile.tier_handle);
    }
    if (Array.isArray(profile.statuses) && profile.statuses.indexOf('team-fave') !== -1) {
      document.body.classList.add('cll-team-fave');
    }
  }
  on('profile:update', applyProfileClasses);

  // ---------------------------------------------------------------------
  // Session store — ONE implementation for wish_list / reserve_list,
  // replaces the duplicated `cookie` / `reserveCookie` objects that used
  // to live in theme.js's Shopify.RecordList.
  //
  // Entry format is unchanged from the legacy system so existing cookies
  // on returning visitors keep working: "handle:productId:variantId"
  // ---------------------------------------------------------------------
  var Store = {
    _cookieName: function (type) {
      return config.cookiePrefix + type;
    },
    read: function (type) {
      if (!window.Cookies) { warn('Cookies library not found.'); return []; }
      var raw = window.Cookies.get(this._cookieName(type));
      return raw ? raw.split(' ').filter(Boolean) : [];
    },
    write: function (type, items) {
      if (!window.Cookies) { warn('Cookies library not found.'); return; }
      window.Cookies.set(this._cookieName(type), items.join(' '), {
        expires: config.cookieExpiryDays,
        path: '/',
        domain: window.location.hostname
      });
      emit('session:updated', { type: type, items: items, count: items.length });
    },
    clear: function (type) {
      if (!window.Cookies) return;
      window.Cookies.remove(this._cookieName(type), { path: '/', domain: window.location.hostname });
      emit('session:updated', { type: type, items: [], count: 0 });
    },
    add: function (type, key) {
      var items = this.read(type);
      var idx = items.indexOf(key);
      if (idx !== -1) items.splice(idx, 1);
      items.unshift(key);
      this.write(type, items);
      return items;
    },
    remove: function (type, key) {
      var items = this.read(type);
      var idx = items.indexOf(key);
      if (idx !== -1) {
        items.splice(idx, 1);
        this.write(type, items);
      }
      return items;
    },
    findByVariant: function (type, variantId) {
      return this.read(type).find(function (entry) {
        var parts = entry.split(':');
        return parts[2] === String(variantId);
      });
    },
    has: function (type, variantId) {
      return !!this.findByVariant(type, variantId);
    },
    count: function (type) {
      return this.read(type).length;
    }
  };

  // ---------------------------------------------------------------------
  // Auth — Phase 1: token via Omneo's Shopify app proxy (GET /apps/cx)
  //
  // Replaces the old flow of computing an HMAC signature in Liquid and
  // POSTing it to a Cloud Function. That signature no longer needs to
  // exist anywhere in page source.
  //
  // Response shape confirmed on staging (2026-07-01): flat JSON —
  // { token, exp, profile }. See fetchTokenViaProxy() below.
  // ---------------------------------------------------------------------
  function tokenKey() { return 'shapes:omneo:shapestoken:' + (config.customerId || 0); }
  function expiryKey() { return 'shapes:omneo:shapestoken:expiry:' + (config.customerId || 0); }

  function isTokenExpired() {
    var expiry = parseInt(localStorage.getItem(expiryKey()), 10);
    if (!expiry) return true;
    return Math.round(Date.now() / 1000) > expiry;
  }

  function getStoredToken() { return localStorage.getItem(tokenKey()); }

  function clearStoredToken() {
    localStorage.removeItem(tokenKey());
    localStorage.removeItem(expiryKey());
  }

  // Confirmed shape (staging, 2026-07-01): GET /apps/cx returns a flat
  // object — { token, exp, profile } — no nesting under `data`. `exp` is
  // a Unix timestamp in seconds, matching isTokenExpired()'s comparison.
  // As a bonus it includes the full Omneo profile inline, so we don't
  // need to wait on a separate shapesClient hydrate('profile') round
  // trip just to get tier/preferred-store — see initShapesSDK() below,
  // which emits profile:update immediately from this payload.
  function fetchTokenViaProxy() {
    return fetch(config.tokenProxyUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('Token proxy responded ' + res.status);
      return res.json();
    }).then(function (payload) {
      if (!payload.token) throw new Error('/apps/cx response did not contain a token.');
      localStorage.setItem(tokenKey(), payload.token);
      if (payload.exp) localStorage.setItem(expiryKey(), payload.exp);
      if (payload.profile) emit('profile:update', payload.profile);
      return payload.token;
    });
  }

  function fetchTokenWithRetry(retriesLeft) {
    retriesLeft = retriesLeft === undefined ? 1 : retriesLeft;
    return fetchTokenViaProxy().catch(function (err) {
      if (retriesLeft > 0) {
        log('Token fetch failed, retrying once.', err);
        return fetchTokenWithRetry(retriesLeft - 1);
      }
      throw err;
    });
  }

  // ---------------------------------------------------------------------
  // Guest → logged-in wishlist merge
  //
  // Found via testing (screenshots comparing a guest wishlist vs. the
  // same browser after login): guest wishlist items are written to the
  // session cookie only. Logging in switches rendering entirely to
  // Omneo data — nothing ever pushed the guest items into Omneo, so
  // they silently vanished from view the moment someone logged in,
  // despite still technically sitting in the cookie. Real customer
  // impact: "I built a wishlist, logged in like the site told me to,
  // and it's gone."
  //
  // Session cookie entries are `handle:productId:variantId` — no SKU,
  // which both add() and Omneo-side dedup need. Resolved here via
  // /products/{handle}.js, the same pattern used in omneo-lists-cards.js
  // for guest card rendering, duplicated here since this needs to run
  // sitewide (login can happen on any page, not just the lists page
  // where that module lives) — this module (omneo-lists.js) is the one
  // loaded everywhere.
  // ---------------------------------------------------------------------
  function fetchProductJsonForMerge(handle) {
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Product fetch failed: ' + res.status + ' for ' + handle);
        return res.json();
      });
  }

  // Render-time fetch failures (fetching a saved item's live product
  // data from Shopify, to build its card on the lists page) were
  // previously invisible — console.warn only, in omneo-lists-cards.js.
  // Found via a real report: two clients said they couldn't see items
  // in their Reserve List, but the items were still genuinely saved
  // (confirmed in Omneo). This is exactly what that looks like — the
  // add/remove sync succeeded (nothing to report there), but the
  // SEPARATE later step of fetching that item's product data to
  // display it failed silently, dropping it from view while it still
  // exists in Omneo. Routed through the same sync:failed pipeline
  // (action: 'render') so these become visible in the same log/alert
  // system as genuine sync failures, rather than being a second,
  // invisible failure mode.
  function reportRenderFailure(type, handle, err) {
    emit('sync:failed', { type: type, action: 'render', handle: handle, error: err });
  }

  function mergeGuestWishlistIntoOmneo() {
    var guestEntries = Store.read('wish_list');
    if (!guestEntries.length || !shapesClient) return Promise.resolve({ mergedCount: 0 });
    if (typeof shapesClient.find !== 'function') {
      warn('shapesClient.find is not a function — cannot merge guest wishlist.');
      return Promise.resolve({ mergedCount: 0 });
    }

    var cached = shapesClient.find('lists');
    var wishList = cached && cached.data && cached.data['wish-list'];
    var existingSkus = {};
    if (wishList && wishList.items) {
      Object.values(wishList.items).forEach(function (item) {
        var sku = item.product_variant && item.product_variant.sku;
        if (sku) existingSkus[sku] = true;
      });
    }

    var toRemove = [];
    var mergedCount = 0;

    var promises = guestEntries.map(function (entry) {
      var parts = entry.split(':');
      var handle = parts[0], productId = parts[1], variantId = parts[2];

      return fetchProductJsonForMerge(handle).then(function (productJson) {
        var variant = (productJson.variants || []).filter(function (v) {
          return String(v.id) === String(variantId);
        })[0];

        if (!variant || !variant.sku) {
          // Can't resolve a SKU for this entry — nothing more we can
          // do with it, drop it rather than retry forever.
          toRemove.push(entry);
          return;
        }

        if (existingSkus[variant.sku]) {
          // Already in the customer's Omneo wishlist — skip duplicate,
          // safe to drop from the guest cookie. Not counted in
          // mergedCount — nothing new for the customer to see, so this
          // alone shouldn't trigger a reload.
          toRemove.push(entry);
          return;
        }

        return add('wish_list', variantId, variant.sku, { handle: handle, productId: productId })
          .then(function () {
            toRemove.push(entry);
            mergedCount++;
          });
      }).catch(function (err) {
        warn('Failed to merge guest wishlist item — will retry next page load.', handle, err);
        // Deliberately NOT added to toRemove — keep it in the cookie
        // so a transient failure (network blip) gets retried later
        // rather than silently losing the item.
      });
    });

    return Promise.all(promises).then(function () {
      if (toRemove.length) {
        var remaining = Store.read('wish_list').filter(function (e) { return toRemove.indexOf(e) === -1; });
        Store.write('wish_list', remaining);
      }
      return { mergedCount: mergedCount };
    });
  }

  function initShapesSDK(token) {
    if (shapesClient) return Promise.resolve(shapesClient);
    if (!window.ShapesSDK) {
      warn('window.ShapesSDK not available yet.');
      return Promise.resolve(null);
    }
    shapesClient = window.ShapesSDK.init({
      url: config.omneoIdUrl,
      token: token,
      logging: !!config.debug
    });

    // ⚠ DEFENSIVE, added after a live production incident: every
    // shapesClient method call below used to run unguarded. If
    // window.ShapesSDK.init() ever returns an object that doesn't have
    // the expected shape (confirmed to happen at least once on
    // production, cause still being diagnosed — window.ShapesSDK.init()
    // is external, not something this package controls), the very
    // first unguarded call (shapesClient.hydrate(...)) threw and
    // aborted the rest of this function — meaning auth never completed,
    // breaking the entire package on every single page. Each call is
    // now individually guarded: a missing method logs a clear warning
    // and that one capability is skipped, rather than the whole
    // integration going down over one missing method.
    function safeCall(methodName) {
      var args = Array.prototype.slice.call(arguments, 1);
      if (!shapesClient || typeof shapesClient[methodName] !== 'function') {
        warn('shapesClient.' + methodName + ' is not a function — skipping. ' +
          'This usually means window.ShapesSDK.init() returned something unexpected; ' +
          'check that the Shapes SDK script itself loaded correctly and matches the ' +
          'expected version.');
        return undefined;
      }
      return shapesClient[methodName].apply(shapesClient, args);
    }

    // hydrate('profile') still runs even though /apps/cx already gave us
    // a profile:update via fetchTokenViaProxy() — it's what registers
    // the endpoint with the SDK so *future* profile changes (e.g. after
    // a list action) keep emitting shapes.profile.update. The one extra
    // fetch on initial load is a worthwhile trade for that.
    safeCall('hydrate', 'profile');
    safeCall('hydrate', 'lists');
    safeCall('on', 'shapes.profile.update', function (data) { emit('profile:update', data.data); });

    var hasMergedGuestWishlist = false;
    function handleListsData(eventName, data) {
      emit(eventName, data.data);
      if (hasMergedGuestWishlist) return;
      hasMergedGuestWishlist = true;
      // Reload-after-merge removed per request — merge still runs and
      // still pushes guest items into Omneo, it just no longer forces
      // a page reload to show them. Known trade-off, confirmed
      // accepted: since shapesClient.find('lists') doesn't reliably
      // reflect the merge without a reload (same SDK-caching
      // limitation noted throughout this file), merged items may not
      // visually appear until the customer next navigates or reloads
      // manually.
      mergeGuestWishlistIntoOmneo();
    }

    // ⚠ FOUND VIA TESTING: shapes.lists.ready never actually fires in
    // this environment — confirmed via the raw per-event debug console
    // log (which captures every emit() call when debug is on): auth:ready,
    // profile:update, and lists:update all appeared, lists:ready never
    // did, across multiple real login sessions. This SDK apparently only
    // emits shapes.lists.update, even for the very first hydrate. The
    // merge hook was originally attached ONLY to shapes.lists.ready,
    // making it dead code — explains why guest items never merged
    // despite no errors anywhere (the code just never ran at all).
    // Wired to BOTH events now, guarded by hasMergedGuestWishlist so it
    // still only actually runs once regardless of which one fires (or
    // if, on some other environment/timing, both do).
    safeCall('on', 'shapes.lists.update', function (data) { handleListsData('lists:update', data); });
    safeCall('on', 'shapes.lists.ready', function (data) { handleListsData('lists:ready', data); });

    // auth:ready still fires even if the calls above were skipped —
    // the token exchange itself succeeded (that's a separate, already-
    // confirmed-working code path via fetchTokenViaProxy), so the
    // product-page/list-page UI can still function using session-cookie
    // data and whatever profile data /apps/cx already provided, even in
    // a degraded state where live Shapes SDK events aren't available.
    emit('auth:ready', { client: shapesClient });
    return Promise.resolve(shapesClient);
  }

  // Runs once per page load. Never blocks rendering — call this from
  // init() and let it resolve in the background. Guests never fetch a
  // token, matching "encourage, don't enforce" login.
  function ensureAuthenticated() {
    if (authPromise) return authPromise;

    if (!config.isLoggedIn) {
      clearStoredToken();
      authPromise = Promise.resolve(null);
      return authPromise;
    }

    var token = getStoredToken();
    if (token && !isTokenExpired()) {
      authPromise = initShapesSDK(token);
      return authPromise;
    }

    authPromise = fetchTokenWithRetry(1)
      .then(initShapesSDK)
      .catch(function (err) {
        warn('Unable to obtain an Omneo token after retry.', err);
        emit('auth:failed', { error: err });
        return null;
      });

    return authPromise;
  }

  // ---------------------------------------------------------------------
  // Public list operations
  // ---------------------------------------------------------------------
  var LIST_PATHS = {
    wish_list: 'lists.wish-list.items',
    reserve_list: 'lists.reserve-list.items',
    recommended_list: 'lists.recommended-for-you-list.items'
  };

  function syncEnabled(methodName) {
    if (!config.isLoggedIn || !shapesClient) return false;
    // Also verify the specific method exists — same defensive reasoning
    // as initShapesSDK's safeCall() and refreshLists(): a malformed
    // shapesClient (confirmed to happen at least once on production)
    // could be truthy but missing methods, and calling a missing method
    // throws synchronously — NOT as a promise rejection — so existing
    // .catch() chains on the result wouldn't even catch it.
    if (methodName && typeof shapesClient[methodName] !== 'function') {
      warn('shapesClient.' + methodName + ' is not a function — sync skipped.');
      return false;
    }
    return true;
  }

  function add(type, variantId, sku, meta) {
    meta = meta || {};
    var key = [meta.handle || '', meta.productId || '', variantId].join(':');
    Store.add(type, key);
    emit('add', { type: type, variantId: variantId, sku: sku });

    if (!syncEnabled('post') || !sku) return Promise.resolve({ synced: false });

    return shapesClient.post(LIST_PATHS[type], { product_variant_sku: sku }, false)
      .then(function (response) {
        // Found and fixed a real bug: this used to discard the response
        // entirely. Without the newly-created Omneo item's ID, a
        // subsequent remove (within the same page session, before any
        // reload) had nothing to tell Omneo which record to delete —
        // the Omneo-side delete silently no-op'd while the UI showed
        // the item as removed. Add it back and a second Omneo record
        // gets created, orphaning the first — repeat and records pile
        // up. Confirmed via a real report of duplicate backend records.
        //
        // Confirmed shape via a real staging response, 2026-07-02:
        // { status: 201, message: '', error: false, body: { data: { id: 48387, ... } } }
        var omneoItemId = response && response.body && response.body.data && response.body.data.id;
        emit('sync:success', { type: type, action: 'add', sku: sku, variantId: variantId, omneoItemId: omneoItemId, title: meta.title });
        return { synced: true, omneoItemId: omneoItemId };
      })
      .catch(function (err) {
        warn('Failed to sync add to Omneo.', err);
        emit('sync:failed', { type: type, action: 'add', sku: sku, variantId: variantId, title: meta.title, error: err });
        return { synced: false, error: err };
      });
  }

  function remove(type, variantId, omneoItemId, meta) {
    meta = meta || {};
    var match = Store.findByVariant(type, variantId);
    if (match) Store.remove(type, match);
    emit('remove', { type: type, variantId: variantId });

    if (!syncEnabled('delete') || !omneoItemId) return Promise.resolve({ synced: false });

    return shapesClient.delete(LIST_PATHS[type] + '.' + omneoItemId, false)
      .then(function () {
        emit('sync:success', { type: type, action: 'remove', variantId: variantId, omneoItemId: omneoItemId, title: meta.title });
        return { synced: true };
      })
      .catch(function (err) {
        warn('Failed to sync remove from Omneo.', err);
        emit('sync:failed', { type: type, action: 'remove', variantId: variantId, omneoItemId: omneoItemId, title: meta.title, error: err });
        return { synced: false, error: err };
      });
  }

  function isSaved(type, variantId) { return Store.has(type, variantId); }
  function getCount(type) { return Store.count(type); }
  function clearSession(type) { Store.clear(type); }

  // Returns every session entry matching a given product ID, parsed into
  // { raw, handle, productId, variantId }. Used by the UI module to
  // restore wishlist/reserve-list visual state (swatch highlighting,
  // button "added" state) when a product page loads — matches by
  // productId, not variantId, since e.g. the reserve-list one-size rule
  // needs to know "is ANY size of this product already saved", not just
  // the currently selected one.
  function getEntriesForProduct(type, productId) {
    return Store.read(type)
      .map(function (entry) {
        var parts = entry.split(':');
        return { raw: entry, handle: parts[0], productId: parts[1], variantId: parts[2] };
      })
      .filter(function (e) { return String(e.productId) === String(productId); });
  }

  // Returns every session entry for a list type, not scoped to a
  // product. Used by the lists-page renderer (guest path) to build the
  // full grid from the session cookie.
  function getAllEntries(type) {
    return Store.read(type).map(function (entry) {
      var parts = entry.split(':');
      return { raw: entry, handle: parts[0], productId: parts[1], variantId: parts[2] };
    });
  }

  // Forces a fresh Omneo lists fetch and reliably re-emits lists:update
  // once done — added because relying on the Shapes SDK's own
  // shapes.lists.update event to fire again on a manual re-hydrate
  // (rather than the initial auth-time one) turned out not to be
  // reliable in practice (found via staging testing: a "Refresh list"
  // click left the UI stuck in its loading state, and destination
  // sections after a move never updated — both traced to this event
  // simply never firing on demand). This explicitly re-checks
  // shapesClient.find('lists') after hydrate() settles and emits
  // lists:update itself, rather than depending on the SDK to do it.
  //
  // ⚠ ASSUMPTION: shapesClient.hydrate() returning a thenable isn't
  // confirmed against the SDK's actual behavior/docs — handled
  // defensively with a timeout-based fallback re-check in case it
  // doesn't. Worth confirming directly if this still doesn't fully
  // resolve the staleness issue.
  function refreshLists() {
    if (!shapesClient) return Promise.resolve(null);

    function reEmitFromCache() {
      if (typeof shapesClient.find !== 'function') {
        warn('shapesClient.find is not a function — cannot refresh lists.');
        return null;
      }
      var cached = shapesClient.find('lists');
      if (cached && cached.data) emit('lists:update', cached.data);
      return cached;
    }

    if (typeof shapesClient.hydrate !== 'function') {
      warn('shapesClient.hydrate is not a function — cannot refresh lists.');
      return Promise.resolve(null);
    }

    var result = shapesClient.hydrate('lists');
    if (result && typeof result.then === 'function') {
      return result.then(reEmitFromCache);
    }
    // Fallback: hydrate() didn't return a thenable — re-check shortly
    // after instead of assuming synchronous completion.
    return new Promise(function (resolve) {
      setTimeout(function () { resolve(reEmitFromCache()); }, 800);
    });
  }

  // ---------------------------------------------------------------------
  // Stale-login recovery
  //
  // Found via a real bug report: guest wishlist items weren't merging
  // into Omneo after login — but only when login happened via the
  // header account icon (the iframe slideout), not when it happened
  // via an actual page navigation (e.g. the lists page's own "login to
  // your profile" link).
  //
  // Root cause: config.isLoggedIn is server-rendered Liquid, baked into
  // the page at load time — it can't update itself client-side. Traced
  // the actual login flow in omneo-profile-portal.liquid: the 'logout'
  // postMessage handler explicitly reloads the page
  // (window.location.href = refreshUrl), but the 'login' handler never
  // does — it only toggles DOM classes and calls
  // redirectToCheckoutOnLogin() (which only navigates for checkout-
  // specific scenarios, not normal logins). So after an iframe login on
  // any other page, this package keeps believing the customer is a
  // guest — config.isLoggedIn never becomes true client-side — meaning
  // the entire authenticated flow (including the guest-wishlist-merge-
  // on-login feature) never even attempts to run until the customer
  // happens to navigate somewhere and gets a fresh, correctly-rendered
  // page.
  //
  // Fixed here, not in omneo-profile-portal.liquid — deliberately not
  // touching that file's existing login/checkout-redirect logic, which
  // is revenue-critical and already has its own careful conditional
  // navigation behaviour. This listens independently for the same
  // 'login' postMessage and self-heals via a reload, but only when:
  //   (a) this package currently believes the customer is a guest
  //       (config.isLoggedIn is false) — avoids reloading for customers
  //       already correctly recognised as logged in, and
  //   (b) the URL doesn't indicate a checkout-triggered login already
  //       in progress — that flow will itself cause a full navigation
  //       to checkout, which naturally refreshes this stale config too;
  //       reloading here as well could race with that redirect and
  //       send the customer to a reloaded current page instead of
  //       through to checkout.
  // ---------------------------------------------------------------------
  window.addEventListener('message', function (event) {
    if (!event.origin || event.origin.indexOf('juliettehogan.com') === -1) return;
    if (event.data !== 'login') return;
    if (config.isLoggedIn) return; // already correctly recognised — nothing stale to fix
    if (window.location.href.indexOf('checkout_url') > -1) return; // let the existing checkout redirect happen instead

    // ⚠ LOOP-PREVENTION GUARD — added urgently after a real production
    // incident: this reload looped indefinitely for some customers.
    // Likely cause: the iframe may send 'login' every time the drawer
    // opens, not just on a genuine new login, and if the underlying
    // config.isLoggedIn mismatch doesn't actually resolve after a
    // reload for some reason, the same message on the reloaded page
    // triggers another reload, forever. sessionStorage persists across
    // the reload but clears when the tab/session ends, so this allows
    // AT MOST ONE reload attempt per browser session — breaking any
    // loop instead of repeating it. If reloading once doesn't fix the
    // mismatch, that's a real underlying problem needing investigation,
    // not something more reloads would solve anyway.
    var LOOP_GUARD_KEY = 'omneolists_stale_login_reload_attempted';
    try {
      if (window.sessionStorage.getItem(LOOP_GUARD_KEY)) {
        warn('Stale-login reload already attempted this session — not reloading again, to avoid a loop. The underlying config.isLoggedIn mismatch may not be resolving on reload; needs investigation.');
        return;
      }
      window.sessionStorage.setItem(LOOP_GUARD_KEY, '1');
    } catch (e) {
      // sessionStorage unavailable (private browsing, disabled, etc.) —
      // fail safe by NOT reloading at all rather than risk an
      // unguarded loop with no way to break it.
      warn('sessionStorage unavailable — skipping stale-login reload to avoid a potential unguarded loop.');
      return;
    }

    window.location.reload();
  });

  // ---------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------
  function init() {
    readConfig();
    log('Initialising with config', config);

    // Clear the stale-login reload guard once login is genuinely,
    // correctly recognised on this page load — allows the mechanism a
    // fresh single attempt for a future real stale-login event later in
    // the same session (e.g. the customer logs out and back in again),
    // rather than being permanently disabled for the rest of the
    // session after one earlier attempt. Placed here, not at the
    // listener's registration point above — config isn't populated
    // until readConfig() runs, which only happens here in init().
    if (config.isLoggedIn) {
      try { window.sessionStorage.removeItem('omneolists_stale_login_reload_attempted'); } catch (e) { /* ignore */ }
    }

    ensureAuthenticated();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------
  window.OmneoLists = {
    add: add,
    remove: remove,
    isSaved: isSaved,
    getCount: getCount,
    clearSession: clearSession,
    getEntriesForProduct: getEntriesForProduct,
    getAllEntries: getAllEntries,
    refreshLists: refreshLists,
    reportRenderFailure: reportRenderFailure,
    on: on,
    ready: function () { return ensureAuthenticated(); },
    // Exposed for the UI module and for debugging — not part of the
    // stable public API, may change shape between versions.
    _store: Store,
    _getConfig: function () { return config; },
    _getClient: function () { return shapesClient; }
  };

})(window, document);